import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, IconButton, LinearProgress, Paper, Stack, TextField, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  PhotoCamera as CameraIcon, HourglassEmpty as PendingIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import staffScanService, { isTerminal, StaffScan, StaffScanCandidate } from '../../services/staffScanService';

/**
 * SCANNING AS AN ENTRY METHOD (operator ruling, P5b follow-up).
 *
 * Not a destination — a step inside the flows that already exist. It
 * replaces TYPING A SEARCH: read the bottle, resolve or draft the wine,
 * hand it back to the host flow with the wine selected. The host owns
 * everything after that (quantity, size, price on the venue side; vintage
 * and save on the admin side).
 *
 * Written for a phone held one-handed at a receiving dock: full-width
 * controls, large tap targets, and NO hover affordance anywhere — a
 * tooltip is invisible under a thumb, so everything that matters is
 * printed. The staff constraints hold on the staff path regardless of
 * where it is mounted: provisional only, never a canonical mint, and no
 * paid ask-the-web button (those are the operator's).
 */

export interface ScannedWine {
  /** Present on the staff path (an identity was resolved or drafted). */
  wineId?: string;
  wineNameId?: string;
  name: string;
  displayVintage: string;
  vintageType: string;
  converged?: boolean;
  provisional?: boolean;
  /** The corrected reading — what the operator path pre-fills from. */
  reading?: { producer: string; region: string; cuvee: string; vintage: string; grapes: string };
}

export interface ScanToWineDialogProps {
  open: boolean;
  /** '' on the admin path: the wine database owns no venue. */
  venueId: string;
  onClose: () => void;
  /** The host flow continues from here with the wine in hand. */
  onResolved: (wine: ScannedWine) => void;
  /**
   * 'staff'  — resolve or DRAFT a provisional identity (venue path).
   * 'operator' — read only, and hand the reading back to pre-fill a form.
   *   The operator has the full console for committing identity; scanning
   *   is only the way in, so nothing is written here.
   */
  mode?: 'staff' | 'operator';
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const isHeic = (f: File) => /image\/hei[cf]/i.test(f.type) || /\.hei[cf]$/i.test(f.name);

const PhotoTile: React.FC<{
  label: string; file: File | null; onPick: (f: File | null) => void; required?: boolean;
}> = ({ label, file, onPick, required }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const url = file ? URL.createObjectURL(file) : null;
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <input
        ref={inputRef} type="file" accept={ACCEPT} capture="environment" hidden
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
      />
      <Button
        fullWidth variant={file ? 'outlined' : 'contained'} size="large"
        startIcon={<CameraIcon />} onClick={() => inputRef.current?.click()}
        sx={{ textTransform: 'none', py: 1.5 }}
      >
        {file ? `${label} ✓` : `${label}${required ? '' : ' (optional)'}`}
      </Button>
      {url && (
        <Box component="img" src={url} alt={label}
          sx={{ mt: 1, width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 1 }} />
      )}
    </Box>
  );
};

const CandidateRow: React.FC<{ c: StaffScanCandidate; picked: boolean; onPick: () => void }> = ({ c, picked, onPick }) => (
  <Paper
    variant="outlined" onClick={onPick}
    sx={{
      p: 1.5, cursor: 'pointer',
      borderColor: picked ? 'primary.main' : undefined,
      borderWidth: picked ? 2 : 1,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="body1" sx={{ fontWeight: picked ? 700 : 500 }}>{c.display}</Typography>
      {c.disambiguation?.provisional && (
        // §4a: the convergence chip — printed, not hovered.
        <Chip size="small" color="warning" icon={<PendingIcon />}
          label="Scanned at another venue — pending curation" />
      )}
      {c.disambiguation?.country && <Chip size="small" variant="outlined" label={c.disambiguation.country} />}
      {picked && <Chip size="small" color="primary" label="chosen" />}
    </Stack>
  </Paper>
);

const ScanToWineDialog: React.FC<ScanToWineDialogProps> = ({ open, venueId, onClose, onResolved, mode = 'staff' }) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));

  const [front, setFront] = useState<File | null>(null);
  const [back, setBack] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scan, setScan] = useState<StaffScan | null>(null);
  const [busy, setBusy] = useState(false);

  // Confirm-step state: the reading, as the staff member would correct it.
  const [producer, setProducer] = useState('');
  const [region, setRegion] = useState('');
  const [cuvee, setCuvee] = useState('');
  const [vintage, setVintage] = useState('');
  // The label prints grapes and vision reads them, so the slot is shown.
  // Optional like region — blank is a fine answer — and staff never mint:
  // an unknown grape travels as read text for the operator to resolve.
  const [grapes, setGrapes] = useState('');
  const [pickedWineNameId, setPickedWineNameId] = useState<string | null>(null);

  const pick = (f: File | null, set: (f: File | null) => void) => {
    setError(null);
    if (f && isHeic(f)) {
      setError('That photo is in HEIC format, which we cannot read yet. On iPhone: Settings → Camera → Formats → Most Compatible, then retake.');
      return;
    }
    set(f);
  };

  const startScan = async () => {
    if (!front) return;
    setBusy(true); setError(null);
    try {
      const created = await staffScanService.upload(venueId, front, back);
      setScan(created);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'The upload failed. Check the signal and try again.');
      setBusy(false);
    }
  };

  // Poll until the read finishes. No websocket ceremony; the pipeline is
  // seconds, and a phone on venue wifi survives a 2s poll.
  useEffect(() => {
    if (!scan || isTerminal(scan.status)) { if (scan) setBusy(false); return; }
    const t = setTimeout(async () => {
      try { setScan(await staffScanService.get(scan._id)); } catch { /* keep polling */ }
    }, 2000);
    return () => clearTimeout(t);
  }, [scan]);

  // Seed the editable reading once the scan lands.
  const seeded = useRef(false);
  useEffect(() => {
    if (!scan || !isTerminal(scan.status) || seeded.current) return;
    seeded.current = true;
    const r = scan.visionRead ?? {};
    setProducer(String(r.producer?.text ?? ''));
    setRegion(String(r.appellation?.text ?? ''));
    setCuvee(String(r.specialName?.text ?? ''));
    setVintage(r.vintage?.value != null ? String(r.vintage.value) : '');
    // 'printed' only — SOP forbids inferring grapes that are not on the label.
    const lg: any = (r as any).labelGrapes;
    setGrapes(lg?.evidence === 'printed' ? (lg.values ?? []).join(', ') : '');
  }, [scan]);

  const recordCorrection = useCallback(async (slot: string, from: unknown, to: unknown, kind: 'edit' | 'vintage' | 'pick_candidate') => {
    if (!scan || !venueId) return;    // corrections are venue-scoped by design
    if (String(from ?? '') === String(to ?? '')) return;
    try {
      await staffScanService.addCorrection(venueId, scan._id, {
        slot, from: (from ?? null) as any, to: (to ?? null) as any, kind,
      });
    } catch { /* a correction that fails to record must not block the item */ }
  }, [scan, venueId]);

  const accept = async () => {
    if (!scan) return;
    // Operator path: no write at all — hand back the reading and the
    // candidate they picked, and let the admin form do the committing.
    if (mode === 'operator') {
      const picked = candidates.find((c) => c.identityRef.wineNameId === pickedWineNameId);
      onResolved({
        wineNameId: pickedWineNameId ?? undefined,
        name: picked?.display ?? [producer.trim(), cuvee.trim()].filter(Boolean).join(' '),
        displayVintage: vintage.trim(),
        vintageType: vintage.trim() ? 'single_vintage' : 'non_vintage',
        reading: {
          producer: producer.trim(), region: region.trim(), cuvee: cuvee.trim(),
          vintage: vintage.trim(), grapes: grapes.trim(),
        },
      });
      reset();
      onClose();
      return;
    }
    setBusy(true); setError(null);
    try {
      const res = await staffScanService.accept(venueId, scan._id, {
        ...(pickedWineNameId ? { wineNameId: pickedWineNameId } : {}),
        fields: {
          producerName: producer.trim() || undefined,
          regionName: region.trim() || undefined,
          specialName: cuvee.trim() || undefined,
          vintage: vintage.trim() ? Number(vintage.trim()) : null,
          grapes: grapes.trim() ? grapes.split(',').map((g) => g.trim()).filter(Boolean) : undefined,
        },
        // The host flow owns the item; scanning only resolves the wine.
        createItem: false,
      });
      onResolved({
        wineId: res.wineId, wineNameId: res.wineNameId,
        name: res.wine?.name ?? producer.trim(),
        displayVintage: res.wine?.displayVintage ?? '',
        vintageType: res.wine?.vintageType ?? 'non_vintage',
        converged: res.converged, provisional: res.provisional,
      });
      reset();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not add the wine. Try again, or ask an admin.');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setScan(null); setFront(null); setBack(null); setError(null);
    setPickedWineNameId(null); setGrapes(''); seeded.current = false;
  };

  const candidates = scan?.resolution?.candidates ?? [];
  const flags = scan?.resolution?.flags ?? [];
  const reading = scan && isTerminal(scan.status);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullScreen={phone} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        Scan the label
        <IconButton
          onClick={onClose} disabled={busy}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Capture ───────────────────────────────────────────────────── */}
      {!scan && (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Photograph the front of the bottle. Add the back label too if the front is hard to read.
          </Typography>
          <Stack direction={phone ? 'column' : 'row'} spacing={2}>
            <PhotoTile label="Front label" file={front} onPick={(f) => pick(f, setFront)} required />
            <PhotoTile label="Back label" file={back} onPick={(f) => pick(f, setBack)} />
          </Stack>
          <Button
            fullWidth size="large" variant="contained" disabled={!front || busy}
            onClick={startScan} sx={{ textTransform: 'none', py: 1.5 }}
          >
            {busy ? 'Sending…' : 'Read this label'}
          </Button>
        </Stack>
      )}

      {/* ── Reading in flight ─────────────────────────────────────────── */}
      {scan && !reading && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Typography variant="body1">Reading the label…</Typography>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            This takes a few seconds. You can keep the phone in your hand.
          </Typography>
        </Stack>
      )}

      {/* ── Confirm ───────────────────────────────────────────────────── */}
      {reading && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          {scan.images?.[0]?.url && (
            <Box component="img" src={scan.images[0].url} alt="the label you photographed"
              sx={{ width: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: 1, bgcolor: 'action.hover' }} />
          )}

          {flags.map((f, i) => (
            <Alert key={i} severity="info">{f.userMessage || f.detail}</Alert>
          ))}

          {candidates.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Is it one of these?</Typography>
              <Stack spacing={1}>
                {candidates.filter((c) => c.identityRef.wineNameId).map((c, i) => (
                  <CandidateRow
                    key={i} c={c}
                    picked={pickedWineNameId === c.identityRef.wineNameId}
                    onPick={() => {
                      const id = c.identityRef.wineNameId!;
                      const next = pickedWineNameId === id ? null : id;
                      setPickedWineNameId(next);
                      if (next) void recordCorrection('wineName', null, c.display, 'pick_candidate');
                    }}
                  />
                ))}
              </Stack>
              <Divider sx={{ my: 2 }}>or check the details below</Divider>
            </Box>
          )}

          <Typography variant="subtitle2">What the label says</Typography>
          <TextField
            fullWidth label="Producer" value={producer}
            onChange={(e) => setProducer(e.target.value)}
            onBlur={() => recordCorrection('producer', scan.visionRead?.producer?.text ?? '', producer, 'edit')}
            helperText="Correct it if we misread. An admin confirms the spelling later."
          />
          <TextField
            fullWidth label="Wine or cuvée (optional)" value={cuvee}
            onChange={(e) => setCuvee(e.target.value)}
            onBlur={() => recordCorrection('specialName', scan.visionRead?.specialName?.text ?? '', cuvee, 'edit')}
          />
          <TextField
            fullWidth label="Region (optional)" value={region}
            onChange={(e) => setRegion(e.target.value)}
            onBlur={() => recordCorrection('appellation', scan.visionRead?.appellation?.text ?? '', region, 'edit')}
            helperText="Leave it blank if you are not sure — an admin will fill it in."
          />
          <TextField
            fullWidth label="Grapes (optional)" value={grapes}
            onChange={(e) => setGrapes(e.target.value)}
            onBlur={() => recordCorrection(
              'labelGrapes',
              ((scan.visionRead as any)?.labelGrapes?.values ?? []).join(', '),
              grapes,
              'edit',
            )}
            helperText="Only what the label prints. Separate two or three with commas; blank is fine."
          />
          <TextField
            fullWidth label="Vintage" value={vintage} inputMode="numeric"
            onChange={(e) => setVintage(e.target.value)}
            onBlur={() => recordCorrection('vintage', scan.visionRead?.vintage?.value ?? '', vintage, 'vintage')}
            helperText="Blank means non-vintage."
          />

          <Button
            fullWidth size="large" variant="contained" disabled={busy || (!producer.trim() && !pickedWineNameId)}
            onClick={accept} sx={{ textTransform: 'none', py: 1.5 }}
          >
            {busy ? 'Working…' : 'Use this wine'}
          </Button>
          <Typography variant="caption" color="text.secondary" align="center">
            {mode === 'operator'
              ? 'The form opens pre-filled with this reading. Nothing is saved until you save it.'
              : 'You can finish the rest as normal. An admin confirms the wine’s details afterwards — you do not need to wait for them.'}
          </Typography>
          {busy && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
        </Stack>
      )}
      </DialogContent>
    </Dialog>
  );
};

export default ScanToWineDialog;
